import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { last, initial, toPlainObject } from 'lodash';

import { PATH } from '../../../constants/path';
import { CONTEXT_ROOT } from '../../../constants';
import { PATH_TO_PARTNER } from '../../../services/i18n/constants';
import { STORAGE_KEY_FAILURE_BANK_TRANSFER_ENABLED } from '../../../constants/storage';
import { FICHE_MARCHAND, FICHE_MARCHAND_OFFLINE } from '../../../services/mea/constants';
import { ENTERPRISE_CARD_NAME, ENTERPRISE_CARD_PARTNER_EBC_ID } from '../../../constants/ebon';

import {
    fetcheBonsByPartnerId,
    fetcheFAQByPartnerId,
    fetchEbonSeoByPartnerId
} from '../../../services/ebon/actions';
import {
    fetchPartnerDetailsIfNeeded,
    fetchOfflinePartnerIfNeeded
} from '../../../services/partner/actions';
import {
    fetchPartnerDiscountsIfNeeded,
    forceFetchDiscounts
} from '../../../services/discount/actions';
import { fetchMeasIfNeeded } from '../../../services/mea/actions';
import { fetchPartnerShopsIfNeeded } from '../../../services/shop/actions';
import { fetchPartnerCouponsIfNeeded } from '../../../services/coupon/actions';

import {
    getPartner,
    getOfflinePartnerByMoId,
    getEbonPartner
} from '../../../services/partner/selector';
import { getI18nByKey } from '../../../services/i18n/selector';
import { getMeasListByTypeId } from '../../../services/mea/selector';

import { isNormalInteger } from '../../../utils/index';
import { isFailureBankTransferEnabled } from '../../../utils/storageUtils';
import { displayableCashbackComplete } from '../../../services/partner/utils';
import { convertHtmlToReactDomWithParams, removeListMarker } from '../../../utils/htmlUtils';

import ShopsMap from './ShopsMap';
import HeaderComponent from './HeaderComponent';
import HeadComponent from '../../HeadComponent';
import StepEbonComponent from './StepEbonComponent';
import { withTemplate } from '../../template/withTemplate';
import HowItWorksComponent from '../../HowItWorksComponent';
import FaqElementComponent from '../faq/FaqElementComponent';
import DiscountsListComponent from './DiscountsListComponent';
import CouponSectionComponent from './CouponSectionComponent';
import DiscoverOfferComponent from './DiscoverOfferComponent';
import { PrimaryTemplate } from '../../template/PrimaryTemplate';
import MeaMerchantCardComponent from './MeaMerchantCardComponent';
import { LoadingComponent } from '../../loading/LoadingComponent';
import EnterpriseCardHowItWorks from './EnterpriseCardHowItWorks';
import CustomerServiceComponent from '../../CustomerServiceComponent';
import { withLoadingCondition } from '../../loading/withLoadingCondition';
import EbonSelectorComponent from '../ebon/ebonselector/EbonSelectorComponent';
import LocalizedBreadcrumbItem from '../../breadcrumb/LocalizedBreadcrumbsItem';

import './MerchantCardPage.scss';

const getPartnerIdFromQuery = query => last(query.split('-'));
const getPartnerNameFromQuery = query => initial(query.split('-')).join('-');

const parseHashfromQuery = hash => {
    const hashValue = hash.slice(1);
    return hashValue != null ? hashValue : null;
};

export class MerchantCardPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            partnerId: -1,
            partnerName: ''
        };
    }

    scrollToId = id => {
        if (document.getElementById(id)) {
            document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
        }
    };

    componentWillMount() {
        this.initPage(
            getPartnerIdFromQuery(this.props.match.params.query),
            getPartnerNameFromQuery(this.props.match.params.query)
        );
    }

    componentDidMount() {
        const { location } = this.props;

        const hash = parseHashfromQuery(location.hash);
        if (hash != null)
            setTimeout(() => {
                this.scrollToId(hash);
            }, 500);
    }

    componentDidUpdate(prevProps) {
        const {
            partner,
            ebonPartner,
            ebons,
            history,
            fetching,
            ebonPartnerFetching,
            isOffline,
            isEbon,
            fetchPartnerDetailsIfNeeded,
            fetchOfflinePartnerIfNeeded,
            fetchMeasIfNeeded,
            fetchPartnerShopsIfNeeded,
            shops,
            offlinePartner
        } = this.props;

        if (
            (!isEbon && !fetching && !partner) ||
            (isEbon && !ebonPartnerFetching && !ebonPartner)
        ) {
            history.push(PATH.NOT_FOUND_PATH);
        } else if (partner) {
            fetchPartnerDetailsIfNeeded(partner.id);
        }

        if (prevProps.isOffline !== isOffline && isOffline && !shops && !offlinePartner) {
            fetchPartnerShopsIfNeeded(partner.id);
            fetchMeasIfNeeded(FICHE_MARCHAND_OFFLINE);
            fetchOfflinePartnerIfNeeded(partner.id);
        }

        if (prevProps.isEbon !== isEbon && isEbon) {
            fetcheBonsByPartnerId(partner.id);
            fetcheFAQByPartnerId(partner.id);
            fetchEbonSeoByPartnerId(partner.id);
        }

        if (!isFailureBankTransferEnabled() && isEbon && ebons && ebons.ebonPaymentConfig) {
            sessionStorage.setItem(
                STORAGE_KEY_FAILURE_BANK_TRANSFER_ENABLED,
                ebons.ebonPaymentConfig.failureBankTransferEnabled
            );
        }
    }

    componentWillReceiveProps = nextProps => {
        const { history, isAuthenticated, forceFetchDiscounts } = this.props;
        const { partnerId, partnerName } = this.state;
        const partnerIdUrl = getPartnerIdFromQuery(nextProps.match.params.query);
        const partnerNameUrl = getPartnerNameFromQuery(nextProps.match.params.query);

        if (!isNormalInteger(partnerIdUrl)) {
            history.push(PATH.NOT_FOUND_PATH);
        } else if (partnerId !== partnerIdUrl && partnerName !== partnerNameUrl) {
            this.initPage(partnerIdUrl, partnerNameUrl);
        }

        if (isAuthenticated !== nextProps.isAuthenticated) {
            forceFetchDiscounts(partnerId);
        }
    };

    initPage(partnerId, partnerName) {
        const {
            fetchMeasIfNeeded,
            fetchPartnerCouponsIfNeeded,
            fetchPartnerDiscountsIfNeeded,
            fetchOfflinePartnerIfNeeded,
            fetcheBonsByPartnerId,
            fetchPartnerShopsIfNeeded,
            fetcheFAQByPartnerId,
            fetchEbonSeoByPartnerId,
            isOffline,
            isEbon
        } = this.props;
        this.setState({ partnerId, partnerName });
        fetchMeasIfNeeded(FICHE_MARCHAND);
        fetchPartnerCouponsIfNeeded(partnerId);
        fetchPartnerDiscountsIfNeeded(partnerId);
        if (isOffline) {
            fetchPartnerShopsIfNeeded(partnerId);
            fetchMeasIfNeeded(FICHE_MARCHAND_OFFLINE);
            fetchOfflinePartnerIfNeeded(partnerId);
        }
        if (isEbon) {
            fetcheBonsByPartnerId(partnerId);
            fetcheFAQByPartnerId(partnerId);
            fetchEbonSeoByPartnerId(partnerId);
        }
    }

    renderPage = () => {
        const {
            i18nPage,
            i18nCustomerService,
            i18nPageHowItWorks,
            merchantCardMea,
            partner,
            offlinePartner,
            CASHBACK,
            partnerDisplayableCashbackComplete,
            match,
            user,
            shops,
            shopStatus,
            ebonPartner,
            ebons,
            ebonFetching,
            shopError,
            faqEbon,
            faqFetching
        } = this.props;

        const { parameters } = ebons;
        const isOffline = match && match.params.type === PATH.OFFLINE;
        const isEbon = match && match.params.type === PATH.EBON;

        const currentPartner = isEbon ? ebonPartner : partner;

        const isEnterpriseCard = currentPartner.ebcId === ENTERPRISE_CARD_PARTNER_EBC_ID;
        const partnerName = isEnterpriseCard ? ENTERPRISE_CARD_NAME : currentPartner.name;

        return (
            <div className="flex-container-v">
                <main className="conteneur conteneur-mobile" id="p-fm">
                    <HeadComponent
                        title={format(
                            i18nPage.TITLE,
                            partnerName,
                            partnerDisplayableCashbackComplete
                        )}
                        description={format(
                            i18nPage.DESCRIPTION,
                            partnerName,
                            partnerDisplayableCashbackComplete
                        )}
                        canonicalLink={`${CONTEXT_ROOT}${PATH.CASHBACK_PATH}/${match.params.type}/${this.state.partnerId}-${this.state.partnerName}`}
                        noIndex
                    />
                    <LocalizedBreadcrumbItem to={`${PATH.CASHBACK_PATH}/${match.params.type}`}>
                        {isOffline
                            ? i18nPage.OFFLINE_BREADCRUMB
                            : isEbon
                            ? i18nPage.EBON_BREADCRUMB
                            : i18nPage.ONLINE_BREADCRUMB}
                    </LocalizedBreadcrumbItem>
                    <LocalizedBreadcrumbItem
                        to={`${PATH.MERCHANTPAGE_PATH}${match.params.type}/${this.state.partnerId}-${this.state.partnerName}`}>
                        {partnerName}
                    </LocalizedBreadcrumbItem>

                    <h1>
                        {isEbon
                            ? convertHtmlToReactDomWithParams(i18nPage.EBON_TYPE, partnerName)
                            : `${CASHBACK} ${partnerName}`}
                    </h1>

                    <HeaderComponent
                        id={this.state.partnerId}
                        isOffline={isOffline}
                        isEbon={isEbon}
                    />

                    {isEbon && ebons && ebons.ebonDiscount && (
                        <EbonSelectorComponent partner={currentPartner} />
                    )}
                    {!isEbon && (
                        <DiscountsListComponent
                            partnerId={this.state.partnerId}
                            isOffline={isOffline}
                        />
                    )}

                    <div id="conditions" />
                    {!isOffline && !isEbon && partner.conditions && (
                        <section>
                            <h3>{i18nPage.CONDITIONS_TITLE}</h3>
                            <div>{convertHtmlToReactDomWithParams(partner.conditions)}</div>
                        </section>
                    )}
                    {isOffline && offlinePartner && offlinePartner.conditions && (
                        <section>
                            <h3>{i18nPage.CONDITIONS_TITLE}</h3>
                            <div>{convertHtmlToReactDomWithParams(offlinePartner.conditions)}</div>
                        </section>
                    )}
                    {isEbon && faqEbon.length > 0 && faqEbon.filter(f => f.conditions).length > 0 && (
                        <section>
                            <h3>{i18nPage.CONDITIONS_TITLE}</h3>
                            <div>
                                {faqEbon
                                    .filter(f => f.conditions)
                                    .map(f =>
                                        convertHtmlToReactDomWithParams(removeListMarker(f.content))
                                    )}
                            </div>
                        </section>
                    )}

                    {isEbon && <p className="buyLargeQuantity">{i18nPage.BUY_LARGE_QUANTITY}</p>}

                    {isEbon && isEnterpriseCard && <EnterpriseCardHowItWorks />}

                    <DiscoverOfferComponent
                        partner={partner}
                        isOffline={isOffline}
                        isEbon={isEbon}
                        ebonPartner={ebonPartner}
                    />

                    <MeaMerchantCardComponent
                        mea={merchantCardMea}
                        partner={currentPartner}
                        isOffline={isOffline}
                    />

                    {((!isOffline && !isEbon && partner.conditions) ||
                        (isOffline && offlinePartner && offlinePartner.conditions)) && <hr />}
                    {!isOffline && !isEbon && (
                        <CouponSectionComponent
                            partnerId={this.state.partnerId}
                            partnerName={this.state.partnerName}
                        />
                    )}

                    {isOffline && shops && (
                        <React.Fragment>
                            <ShopsMap
                                user={user}
                                partner={partner}
                                shops={shops}
                                status={shopStatus}
                                error={shopError}
                            />
                            <hr />
                        </React.Fragment>
                    )}

                    <span id="how" />
                    {isEbon && parameters && (
                        <React.Fragment>
                            <section>
                                <h3>
                                    {convertHtmlToReactDomWithParams(
                                        i18nPage.WHERE_AND_HOW_TITLE,
                                        partnerName
                                    )}
                                </h3>
                                {ebonFetching ? (
                                    <LoadingComponent />
                                ) : (
                                    <StepEbonComponent
                                        partner={currentPartner}
                                        parameters={parameters}
                                    />
                                )}
                            </section>
                        </React.Fragment>
                    )}

                    {!isEbon && partner.description && (
                        <section>
                            <h3>{`${i18nPage.ABOUT_TITLE} ${partner.name}`}</h3>
                            <div>{convertHtmlToReactDomWithParams(partner.description)}</div>
                        </section>
                    )}
                </main>
                {isEbon && (
                    <React.Fragment>
                        <div className="conteneur help-container grid">
                            <div className="faq-container inbl">
                                <div className="faq-bandeau">{i18nPageHowItWorks.QUESTION}</div>
                                <div className="faq-list-container">
                                    {faqFetching ? (
                                        <div className="loading-div">
                                            <LoadingComponent />
                                        </div>
                                    ) : (
                                        faqEbon.length > 0 &&
                                        faqEbon.map(faq => (
                                            <FaqElementComponent
                                                key={faq.id}
                                                faq={faq}
                                                cssClass="question-comment-ca-marche-page"
                                                noListMarker
                                                isEbonFaq
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                            <CustomerServiceComponent
                                i18n={i18nCustomerService}
                                cssClass="merchantCard"
                            />
                        </div>
                    </React.Fragment>
                )}
                <HowItWorksComponent
                    topShadow
                    showTitle
                    isOffline={isOffline}
                    isEbon={isEbon}
                    cssClass="conteneur conteneur-mobile"
                />
            </div>
        );
    };

    render() {
        const {
            fetching = false,
            partner = null,
            ebonPartnerFetching = false,
            ebonPartner = null,
            isEbon
        } = this.props;
        const mustLoad =
            (!isEbon && (fetching || !partner)) ||
            (isEbon && (ebonPartnerFetching || !ebonPartner));

        return withLoadingCondition(mustLoad, this.renderPage);
    }
}

MerchantCardPage.propTypes = {
    i18nPage: PropTypes.object.isRequired,
    coupons: PropTypes.array,
    discounts: PropTypes.array,
    partner: PropTypes.object,
    match: PropTypes.object.isRequired,
    partnerDisplayableCashbackComplete: PropTypes.string,
    merchantCardMea: PropTypes.object,
    user: PropTypes.object
};

const mapStateToProps = (state, ownProps) => {
    const isOffline = ownProps.match && ownProps.match.params.type === PATH.OFFLINE;
    const isEbon = ownProps.match && ownProps.match.params.type === PATH.EBON;

    const partnerId = getPartnerIdFromQuery(ownProps.match.params.query);
    const partner = getPartner(state, partnerId);
    const ebonPartner = getEbonPartner(state, Number(partnerId));

    const meaType = isOffline ? FICHE_MARCHAND_OFFLINE : FICHE_MARCHAND;
    const merchantCardMeas = getMeasListByTypeId(state, meaType).filter(
        mea => mea.idPartenaire === Number(partnerId)
    );

    return {
        CASHBACK: state.i18n.common.CASHBACK,
        i18nPage: state.i18n.page.MERCHANTCARD,
        i18nPageHowItWorks: state.i18n.page.HOW_IT_WORKS,
        i18nCustomerService: state.i18n.feature.aide.serviceclient,
        isAuthenticated: state.authentication.isAuthenticated,
        user: toPlainObject(state.account.user),
        merchantCardMea: merchantCardMeas[0] || null,
        partner,
        isEbon,
        ebons: state.ebon.ebons,
        ebonFetching: state.ebon.ebonsFetching,
        ebonPartner,
        ebonPartners: state.ebon.ebonPartners,
        offlinePartner: getOfflinePartnerByMoId(state, partner && partner.moId),
        partnerDisplayableCashbackComplete: displayableCashbackComplete(
            isEbon ? ebonPartner : partner,
            getI18nByKey(state, PATH_TO_PARTNER),
            isOffline,
            isEbon
        ),
        fetching: state.partner.fetching,
        ebonPartnerFetching: state.ebon.ebonPartnersFetching,
        shops: state.shop.shops[partnerId] ? state.shop.shops[partnerId] : null,
        shopStatus: state.shop.fetching,
        shopError: state.shop.error,
        isOffline: isOffline,
        faqEbon: state.ebon.faq || [],
        faqFetching: state.ebon.faqFetching
    };
};

const mapDispatchToProps = {
    fetchMeasIfNeeded,
    fetchPartnerCouponsIfNeeded,
    fetchPartnerDiscountsIfNeeded,
    forceFetchDiscounts,
    fetchPartnerDetailsIfNeeded,
    fetchOfflinePartnerIfNeeded,
    fetcheBonsByPartnerId,
    fetcheFAQByPartnerId,
    fetchPartnerShopsIfNeeded,
    fetchEbonSeoByPartnerId
};

export default withTemplate(
    connect(mapStateToProps, mapDispatchToProps)(MerchantCardPage),
    PrimaryTemplate
);



// WEBPACK FOOTER //
// ./src/components/pages/merchantcard/MerchantCardPage.js